import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

import "../assets/css/guided-tour.css";

const getNavItem = (label) => {
  const links = Array.from(
    document.querySelectorAll(".modern-nav-link")
  );

  return (
    links.find((link) =>
      link.textContent
        ?.trim()
        .toLowerCase()
        .includes(label.toLowerCase())
    ) || null
  );
};

const getCourseSection = () =>
  document.querySelector(
    ".modern-dashboard-section"
  );

const getWelcomeCard = () =>
  document.querySelector(
    ".modern-welcome-card"
  );

export function GuidedTour({ user }) {
  const userKey =
    user?.id ||
    user?.userId ||
    user?.email ||
    "usuario";

  const storageKey =
    `ps_dashboard_tour_completed_${userKey}`;

  const steps = useMemo(
    () => [
      {
        id: "welcome",
        title: "Bienvenido",
        text:
          "Este es tu centro de capacitación. Desde aquí podrás consultar tus cursos y tu avance.",
        getTarget: getWelcomeCard,
        preferredSide: "bottom",
      },
      {
        id: "courses-nav",
        title: "Mis cursos",
        text:
          "Aquí encontrarás las capacitaciones que tienes asignadas.",
        getTarget: () =>
          getNavItem("Mis cursos"),
        preferredSide: "right",
      },
      {
        id: "courses",
        title: "Continúa tu capacitación",
        text:
          "Selecciona un curso para revisar su contenido, módulos, evaluaciones y avance.",
        getTarget: getCourseSection,
        preferredSide: "top",
      },
      {
        id: "profile",
        title: "Mi perfil",
        text:
          "Mantén actualizados tus datos personales y laborales desde esta sección.",
        getTarget: () =>
          getNavItem("Mi perfil"),
        preferredSide: "right",
      },
      {
        id: "help",
        title: "Ayuda",
        text:
          "Si tienes alguna duda o problema con la plataforma, entra aquí para contactar a Soporte Proceso Seguro.",
        getTarget: () =>
          getNavItem("Ayuda"),
        preferredSide: "right",
      },
    ],
    []
  );

  const [isOpen, setIsOpen] =
    useState(false);

  const [stepIndex, setStepIndex] =
    useState(0);

  const [targetRect, setTargetRect] =
    useState(null);

  const currentStep =
    steps[stepIndex];

  const closeTour = useCallback(
    (completed = false) => {
      if (completed) {
        try {
          localStorage.setItem(
            storageKey,
            "1"
          );
        } catch (error) {
          // La plataforma puede seguir funcionando
          // aunque el navegador bloquee localStorage.
        }
      }

      setIsOpen(false);
      setStepIndex(0);
      setTargetRect(null);
    },
    [storageKey]
  );

  const startTour = useCallback(() => {
    setStepIndex(0);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    let completed = false;

    try {
      completed =
        localStorage.getItem(
          storageKey
        ) === "1";
    } catch (error) {
      completed = false;
    }

    if (!completed) {
      const timer = window.setTimeout(
        () => {
          startTour();
        },
        650
      );

      return () => {
        window.clearTimeout(timer);
      };
    }

    return undefined;
  }, [startTour, storageKey]);

  useEffect(() => {
    const handleManualStart = () => {
      startTour();
    };

    window.addEventListener(
      "ps:start-dashboard-tour",
      handleManualStart
    );

    window.psStartTour = () => {
      window.dispatchEvent(
        new Event(
          "ps:start-dashboard-tour"
        )
      );
    };

    return () => {
      window.removeEventListener(
        "ps:start-dashboard-tour",
        handleManualStart
      );

      if (window.psStartTour) {
        delete window.psStartTour;
      }
    };
  }, [startTour]);

  const updateTarget = useCallback(() => {
    if (!isOpen || !currentStep) {
      return;
    }

    const target =
      currentStep.getTarget?.();

    if (!target) {
      setTargetRect(null);
      return;
    }

    const rect =
      target.getBoundingClientRect();

    setTargetRect({
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    });
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen || !currentStep) {
      return undefined;
    }

    const target =
      currentStep.getTarget?.();

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    const timer = window.setTimeout(
      updateTarget,
      260
    );

    const handlePositionChange = () => {
      updateTarget();
    };

    window.addEventListener(
      "resize",
      handlePositionChange
    );

    window.addEventListener(
      "scroll",
      handlePositionChange,
      true
    );

    return () => {
      window.clearTimeout(timer);

      window.removeEventListener(
        "resize",
        handlePositionChange
      );

      window.removeEventListener(
        "scroll",
        handlePositionChange,
        true
      );
    };
  }, [
    currentStep,
    isOpen,
    stepIndex,
    updateTarget,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeTour(false);
      }

      if (
        event.key === "ArrowRight" &&
        stepIndex < steps.length - 1
      ) {
        setStepIndex(
          (old) => old + 1
        );
      }

      if (
        event.key === "ArrowLeft" &&
        stepIndex > 0
      ) {
        setStepIndex(
          (old) => old - 1
        );
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    closeTour,
    isOpen,
    stepIndex,
    steps.length,
  ]);

  if (
    !isOpen ||
    !currentStep
  ) {
    return null;
  }

  const padding = 7;

  const spotlight = targetRect
    ? {
        top: Math.max(
          7,
          targetRect.top - padding
        ),
        left: Math.max(
          7,
          targetRect.left - padding
        ),
        width: Math.max(
          40,
          targetRect.width +
            padding * 2
        ),
        height: Math.max(
          40,
          targetRect.height +
            padding * 2
        ),
      }
    : null;

  const goNext = () => {
    if (
      stepIndex ===
      steps.length - 1
    ) {
      closeTour(true);
      return;
    }

    setStepIndex(
      (old) => old + 1
    );
  };

  const goPrevious = () => {
    if (stepIndex > 0) {
      setStepIndex(
        (old) => old - 1
      );
    }
  };

  const tour = (
    <div
      className="ps-tour-root"
      aria-live="polite"
    >
      <div className="ps-tour-backdrop" />

      {spotlight && (
        <div
          className="ps-tour-spotlight"
          style={spotlight}
        />
      )}

      <div
        className={`ps-tour-card ${
          spotlight
            ? "has-target"
            : "is-centered"
        }`}
        style={
          spotlight
            ? {
                "--target-top":
                  `${spotlight.top}px`,
                "--target-left":
                  `${spotlight.left}px`,
                "--target-width":
                  `${spotlight.width}px`,
                "--target-height":
                  `${spotlight.height}px`,
                "--preferred-side":
                  currentStep.preferredSide,
              }
            : undefined
        }
        role="dialog"
        aria-modal="true"
        aria-label={
          currentStep.title
        }
      >
        <div className="ps-tour-card-top">
          <span className="ps-tour-step-count">
            PASO {stepIndex + 1} DE{" "}
            {steps.length}
          </span>

          <button
            type="button"
            className="ps-tour-close"
            aria-label="Cerrar recorrido"
            onClick={() =>
              closeTour(false)
            }
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="ps-tour-icon">
          <i className="fas fa-lightbulb" />
        </div>

        <h2>{currentStep.title}</h2>

        <p>{currentStep.text}</p>

        <div className="ps-tour-progress">
          {steps.map(
            (step, index) => (
              <span
                key={step.id}
                className={
                  index === stepIndex
                    ? "active"
                    : index < stepIndex
                    ? "done"
                    : ""
                }
              />
            )
          )}
        </div>

        <div className="ps-tour-actions">
          <button
            type="button"
            className="ps-tour-skip"
            onClick={() =>
              closeTour(true)
            }
          >
            Omitir recorrido
          </button>

          <div className="ps-tour-nav">
            <button
              type="button"
              className="ps-tour-prev"
              disabled={
                stepIndex === 0
              }
              onClick={goPrevious}
            >
              Anterior
            </button>

            <button
              type="button"
              className="ps-tour-next"
              onClick={goNext}
            >
              {stepIndex ===
              steps.length - 1
                ? "Entendido"
                : "Siguiente"}

              <i
                className={
                  stepIndex ===
                  steps.length - 1
                    ? "fas fa-check"
                    : "fas fa-arrow-right"
                }
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    tour,
    document.body
  );
}
